import React, { FC, useEffect } from 'react';
import { isEditorActive, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { TCHSeriesListingDownloadBrochuresProps } from './types/productSeriesListingDownloadBrochures.types';
import { CHSeriesListingDownloadBrochureModel } from './models/productSeriesListingDownloadBrochures.model';
import BrochureCard from '../@shared/productDownloadBrochures/components/brochureCard';
import { isEmptyResponse } from '../../utils/checkEmptyResponse';
import { documentService, windowService } from '../../utils/window.utils';
import EmptyApiDataMessage from '../@shared/emptyApiDataMessage';
import ExperienceEditorComponentPlaceholder from '../@shared/experienceEditorComponentPlaceholder';
import useExperienceEditorPlaceholder from '../../hooks/useExperienceEditorPlaceholder';
import Heading from '../@shared/heading';
import Picture from '../@shared/picture';
import UnreleasedComponent from '../@shared/unreleasedComponent';
import AnalyticsVisibleEvent from '../@shared/analyticsVisibleEvent';
import { lineBrochuresVisible } from '../../constants/analytics.constants';
import useShowProductComponentTitle from '../../hooks/useShowProductComponentTitle';
import { descriptionKeyLineBrochure, eyebrowKeyLineBrochure, titleKeyLineBrochure } from '../../constants/dictionary.constants';
import '../@shared/productDownloadBrochures/styles.scss';

const ProductSeriesListingDownloadBrochures: FC<TCHSeriesListingDownloadBrochuresProps> = (props) => {
  const model = new CHSeriesListingDownloadBrochureModel(props);
  const apiResponse = isEmptyResponse(props);
  const isPlaceholderShown = useExperienceEditorPlaceholder(props);
  const hasHeaderValue = !!(model?.eyebrow?.value || model?.title?.value || model?.description?.value);
  const dictionaryTerms = [titleKeyLineBrochure, eyebrowKeyLineBrochure, descriptionKeyLineBrochure];
  const showHeader = useShowProductComponentTitle(hasHeaderValue, dictionaryTerms);

  useEffect(() => {
    setTimeout(() => {
      const shareScript = 'https://static.addtoany.com/menu/page.js';
      const addthisScript = documentService()?.createElement?.('script');
      addthisScript.setAttribute('src', shareScript);
      const shareContentScript = documentService()?.querySelector?.(`[src="${shareScript}"]`);
      if (documentService()?.body && !shareContentScript) documentService()?.body?.appendChild?.(addthisScript);
      else windowService()?.a2a?.init_all?.();
    });
  }, [model.contextItemUrl]);

  // Component is unreleased
  if (model.isUnreleased) {
    return <UnreleasedComponent />;
  }

  // star node opened directly in EE
  if (isPlaceholderShown) {
    return <ExperienceEditorComponentPlaceholder image={model.thumbnailEE} componentName={model.componentName} />;
  }

  // not opened from star node
  // data missing when navigating via from products node
  if ((!apiResponse || !model.canShowComponent) && isEditorActive()) {
    return <EmptyApiDataMessage componentName={model.componentName} />;
  }

  // No data and not in EE
  if ((!apiResponse && !isPlaceholderShown && !isEditorActive()) || !model.canShowComponent) {
    return null;
  }

  return (
    <AnalyticsVisibleEvent event={lineBrochuresVisible}>
      <section className='product-white-spacer'>
        <div className="download-brochure-container">
          {showHeader && (
            <div className="download-brochure-container__label-container">
              <Picture
                productImageProps={{
                  desktopRendition: model.desktopBgImage,
                  mobileRendition: model.mobileBgImage,
                  pictureClassName: 'download-brochure-container__background-image',
                }}
              />
              <Heading
                eyebrow={model.eyebrow}
                title={model.title}
                subtitle={model.description}
                useDictionaryFallback
                eyebrowDictionaryKey={eyebrowKeyLineBrochure}
                titleDictionaryKey={titleKeyLineBrochure}
                descriptionDictionaryKey={descriptionKeyLineBrochure}
              />
            </div>
          )}

          <div
            className={`download-brochure-container__card-container ${!hasHeaderValue ? 'download-brochure-container__card-container--noHeader' : ''
              }`}
          >
            {model?.downloadBrochureCard?.map((card) => (
              <BrochureCard {...card} key={card.full} isSeries={true}></BrochureCard>
            ))}
          </div>
        </div>
      </section>
    </AnalyticsVisibleEvent>
  );
};

export default withSitecoreContext()(ProductSeriesListingDownloadBrochures);
